// =============================================================================
// SITE-WIDE STRUCTURED DATA (JSON-LD)
// =============================================================================
// Two blocks are injected on EVERY page so search engines correctly identify
// "Pie in the Sky Productions" as a Cape Town FILM & VIDEO PRODUCTION COMPANY
// and NEVER confuse it with the unrelated "Pie in the Sky Bakery".
//
// Re-export individual helpers for nested-page schemas:
//   - breadcrumbSchema(items)
//   - serviceSchema({ name, description, slug })
//   - blogPostingSchema({ title, description, slug, date, image, authorName })
//   - faqSchema(items)
// =============================================================================

import { SITE } from "@/lib/siteData";

const BASE_URL = SITE.url; // https://www.pieinthesky.co.za
const BRAND = "Pie in the Sky Productions";
const LOGO = `${BASE_URL}/logo.png`;
const HERO_IMG = "https://static.wixstatic.com/media/afb734_1f3b60ab75d94464ba94b8af28d37625~mv2.jpg/v1/fill/w_1200,h_630,al_c,q_90,enc_avif,quality_auto/afb734_1f3b60ab75d94464ba94b8af28d37625~mv2.jpg";

// --- Organization (ProfessionalService + LocalBusiness blend) ----------------
// Includes disambiguatingDescription, knowsAbout and sameAs so Google never
// conflates us with the bakery.
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": `${BASE_URL}/#organization`,
  name: BRAND,
  legalName: BRAND,
  alternateName: ["Pie In The Sky Productions", "PITS Productions"],
  url: BASE_URL,
  logo: LOGO,
  image: HERO_IMG,
  description: "Pie in the Sky Productions is a Cape Town film and video production company creating commercials, brand films, corporate video, documentaries, post-production and photography for South African and international clients.",
  disambiguatingDescription: "Pie in the Sky Productions is a film and video production company in Cape Town, South Africa. It is NOT a bakery, food brand, restaurant or pastry shop. The unrelated 'Pie in the Sky Bakery' in Cape Town is a separate, unaffiliated business.",
  slogan: "Cape Town film & video production - cinematic brand stories that captivate and convert.",
  foundingDate: "2014",
  founder: {
    "@type": "Person",
    name: "Rudi Roodt",
  },
  knowsAbout: [
    "Video production",
    "Videography",
    "Commercial production",
    "Brand films",
    "Corporate video",
    "Documentary filmmaking",
    "TV commercials",
    "Online video ads",
    "Cinematography",
    "Film crew hire",
    "Post-production",
    "Video editing",
    "Colour grading",
    "Sound design",
    "Aerial drone cinematography",
    "Commercial photography",
    "Cape Town film production",
    "South African film industry",
  ],
  serviceType: [
    "Video Production",
    "Videography",
    "Commercial Production",
    "Brand Film Production",
    "Corporate Video Production",
    "Documentary Filmmaking",
    "Post-Production",
    "Commercial Photography",
  ],
  areaServed: [
    { "@type": "City",      name: "Cape Town" },
    { "@type": "AdministrativeArea", name: "Western Cape" },
    { "@type": "Country",   name: "South Africa" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Cape Town",
    addressRegion: "Western Cape",
    addressCountry: "ZA",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    email: SITE.email || "info@pieinthesky.co.za",
    telephone: SITE.phone || "+27 82 567 6781",
    areaServed: "ZA",
    availableLanguage: ["English", "Afrikaans"],
  },
  sameAs: [
    SITE.socials?.instagram,
    SITE.socials?.facebook,
    SITE.socials?.linkedin,
    SITE.socials?.youtube,
    SITE.socials?.vimeo,
    SITE.socials?.tiktok,
  ].filter(Boolean),
};

// --- WebSite block (enables sitelinks searchbox + identifies the site entity) -
export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  url: BASE_URL,
  name: BRAND,
  publisher: { "@id": `${BASE_URL}/#organization` },
  inLanguage: "en-ZA",
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE_URL}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

// Convenience: both site-wide blocks together
export const SITE_WIDE_JSONLD = [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA];

// =============================================================================
// PER-PAGE SCHEMA HELPERS
// =============================================================================

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    ...(it.path ? { item: `${BASE_URL}${it.path}` } : {}),
  })),
});

export const serviceSchema = ({ name, description, slug, image }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  serviceType: name,
  provider: { "@id": `${BASE_URL}/#organization` },
  areaServed: [
    { "@type": "City", name: "Cape Town" },
    { "@type": "Country", name: "South Africa" },
  ],
  url: `${BASE_URL}/services/${slug}`,
  ...(image ? { image } : {}),
});

export const blogPostingSchema = ({ title, description, slug, date, image, authorName }) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  description,
  url: `${BASE_URL}/post/${slug}`,
  datePublished: date,
  dateModified: date,
  inLanguage: "en-ZA",
  author: {
    "@type": "Person",
    name: authorName || "Pie in the Sky Productions",
    url: BASE_URL,
  },
  publisher: { "@id": `${BASE_URL}/#organization` },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${BASE_URL}/post/${slug}` },
  ...(image ? { image } : {}),
  about: {
    "@type": "Thing",
    name: "Video production in Cape Town",
  },
});

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((q) => ({
    "@type": "Question",
    name: q.question,
    acceptedAnswer: { "@type": "Answer", text: q.answer },
  })),
});

export const localBusinessSchema = () => ORGANIZATION_SCHEMA; // alias for clarity
